import React, { Dispatch, SetStateAction, useState, useMemo, RefObject } from "react"
import { css } from "@emotion/react"
import {
	useAuth,
	CustomerSite,
	UserId,
	UserProfile,
	useUserProfile,
	useIsUser,
} from "@ncs/ncs-api"
import { formatCurrency } from "@ncs/ts-utils"
import {
	Box,
	Divider,
	Heading,
	Price,
	Paragraph,
	TextInput,
	nextThread,
	Callout,
} from "@ncs/web-legos"
import { useShopContext } from "~/contexts"
import { OrderSummaryRow } from "~/shared-components"
import { PlaceOrderButton } from "./PlaceOrderButton"
import {
	disclaimerStyle,
	headingStyle,
	itemCountStyle,
	orderSummaryCardStyle,
	purchaseOrderOptionalStyle,
	purchaseOrderStyle,
	totalLabelStyle,
	purchaseLabelStyle,
	linkStyle,
} from "./style"
import { Accordion } from "~/shared-components/accordion"
import { VoucherInput, VoucherInputHandle } from "./voucherify/VoucherInput"
import { VoucherifyDiscount } from "../CheckoutEcomm"

export interface OrderSummaryEcommProps {
	isDb?: boolean
	onPlaceOrder: () => Promise<void>
	setErrorMessage: Dispatch<SetStateAction<string | null>>
	shipToSite: CustomerSite | null | undefined
	totalItemCount: number
	orderGrandTotal: number
	shipmentPartsSubtotal: number
	shippingTotal: number
	totalTax: number
	isCanada: boolean
	isSubmitting: boolean
	profile: UserProfile | undefined
	setVoucherifyDiscounts: Dispatch<SetStateAction<VoucherifyDiscount | null>>
	voucherifyDiscounts: VoucherifyDiscount | null | undefined
	voucherInputRef?: RefObject<VoucherInputHandle>
}
export const OrderSummaryEcomm: React.FC<OrderSummaryEcommProps> = ({
	isDb,
	onPlaceOrder,
	orderGrandTotal,
	isCanada,
	setErrorMessage,
	shipToSite,
	isSubmitting,
	totalItemCount,
	shipmentPartsSubtotal,
	shippingTotal,
	totalTax,
	setVoucherifyDiscounts,
	voucherifyDiscounts,
	voucherInputRef,
}) => {
	const auth = useAuth()
	const isPunchout = !!auth.punchOutSessionId
	const isMr = useIsUser(UserId.MisterCarwash)
	const isPunchoutAndMr = isPunchout && isMr
	const [{ cart, checkout }, shopDispatch] = useShopContext()
	const [purchaseOrderNumber, setPurchaseOrderNumber] = useState<string | null>()
	const [profile] = useUserProfile(auth?.user?.id)
	const isCartEmpty = cart.length === 0
	const [isVoucherLoading, setIsVoucherLoading] = useState<boolean>(false)
	const total = isDb ? orderGrandTotal - shippingTotal : orderGrandTotal

	const setTaxValue = () => {
		if (isCartEmpty) {
			return 0
		}
		if (isCanada) {
			return `${formatCurrency(totalTax)} (TBD)`
		}
		return totalTax
	}

	const isPurchaseCapExceeded = useMemo(() => {
		return (
			profile?.purchaseCapIsActive &&
			profile?.purchaseCapAmount &&
			shipmentPartsSubtotal > profile.purchaseCapAmount
		)
	}, [profile?.purchaseCapIsActive, profile?.purchaseCapAmount, shipmentPartsSubtotal])

	const cartTotal = () => {
		if (isCartEmpty) {
			return 0
		} else {
			return voucherifyDiscounts ? voucherifyDiscounts?.totalAmount / 100 : total
		}
	}

	return (
		<div css={orderSummaryCardStyle}>
			<div css={{ marginBottom: "1.5rem" }}>
				<PlaceOrderButton
					onPlaceOrder={onPlaceOrder}
					isLoading={isSubmitting}
					setErrorMessage={setErrorMessage}
					shipToSite={shipToSite}
					orderSubtotal={shipmentPartsSubtotal}
				/>
			</div>
			{isPurchaseCapExceeded && (
				<Callout variant="warning" mb={2}>
					The order subtotal exceeds your maximum allowed purchase amount of $
					{profile?.purchaseCapAmount?.toFixed(2)}. Please adjust your order to proceed.
				</Callout>
			)}
			<Heading mb={1.5} css={headingStyle}>
				Your order
			</Heading>
			<OrderSummaryRow label={`Subtotal`} amount={shipmentPartsSubtotal} />
			<Paragraph css={itemCountStyle}>{`${totalItemCount} item${
				totalItemCount !== 1 ? "s" : ""
			}`}</Paragraph>
			<OrderSummaryRow
				isLoading={isVoucherLoading}
				label={profile && !profile.isGuest ? "Freight(¹)" : "Freight"}
				amount={
					isCartEmpty ? 0
					: isCanada ?
						`${formatCurrency(shippingTotal)} (TBD)`
					:	shippingTotal
				}
			/>
			<OrderSummaryRow
				label={profile && !profile.isGuest ? "Taxes(²)" : "Taxes"}
				amount={setTaxValue()}
			/>
			<Box justifyContent="space-between" mt={1}>
				<OrderSummaryRow
					color="#10A85F"
					isLoading={isVoucherLoading}
					label="Promo savings"
					amount={`$ ${(voucherifyDiscounts?.totalDiscountAmount ?? 0) / 100 || 0}`}
				/>
			</Box>

			<Box display="flex" justifyContent="space-between" mt={1}>
				<Paragraph bold css={totalLabelStyle}>
					Total:
				</Paragraph>
				<Price price={cartTotal()} />
			</Box>

			<Divider />

			<Accordion title="Purchase Order">
				{!isPunchoutAndMr && (
					<div css={purchaseOrderStyle}>
						<div>
							{profile?.poRequired ?
								<p css={purchaseOrderOptionalStyle}>(Required)</p>
							:	<p css={purchaseOrderOptionalStyle}>(Optional)</p>}
						</div>
						<TextInput
							placeholder="Enter PO#"
							ml={1}
							mb={0}
							maxWidth={10}
							value={purchaseOrderNumber}
							css={
								checkout &&
								cart &&
								profile?.poRequired &&
								!checkout.purchaseOrder &&
								cart.length > 0 &&
								(checkout.shipToSiteId || checkout.alternateAddress) &&
								(checkout.selectedPaymentMethod || isPunchout) &&
								css`
									border: 1.5px solid red;
									::placeholder {
										color: red;
									}
								`
							}
							onChange={(value: string | null) => {
								setPurchaseOrderNumber(value)
								nextThread(() =>
									shopDispatch({
										type: "set purchase order",
										payload: value,
									})
								)
							}}
						/>
					</div>
				)}
			</Accordion>
			{/* VOUCHERIFY INPUT COMPONENT */}
			<Accordion title="Promo code">
				<VoucherInput
					ref={voucherInputRef}
					cartTotal={total}
					onDiscountsApplied={setVoucherifyDiscounts}
					setIsVoucherLoading={setIsVoucherLoading}
				/>
			</Accordion>
			<div>
				{profile && !profile.isGuest && (
					<>
						<Paragraph color="#4B5563" css={disclaimerStyle}>
							¹ Freight is estimated and will be reconciled at time of shipping.
						</Paragraph>
						<Paragraph color="#4B5563" css={disclaimerStyle}>
							² Taxes will be assessed at time of invoice.
						</Paragraph>
					</>
				)}
				<Paragraph color="#4B5563" css={disclaimerStyle}>
					By clicking Place Order, you accept our&nbsp;
					<a
						css={linkStyle}
						href="https://ncswash.com/privacy-policy/"
						target="_blank"
						rel="noreferrer"
					>
						Privacy Policy
					</a>
					{" or "}
					<a
						css={linkStyle}
						href="https://ncswash.com/wp-content/uploads/2023/06/NCS-PO-STANDARD-TERMS-AND-CONDITIONS-1.pdf"
						target="_blank"
						rel="noreferrer"
					>
						Terms & Conditions
					</a>
					.
				</Paragraph>
			</div>
		</div>
	)
}
